import { createBaseInputClass } from '@descope-ui/common/base-classes';
import {
  getComponentName,
  forwardAttrs,
  syncAttrs,
} from '@descope-ui/common/components-helpers';
import { connectorMixin } from '@descope-ui/common/components-mixins';
import { compose } from '@descope-ui/common/utils';
import { GoogleMapsConnector, RadarConnector } from '../../connectors';

export const componentName = getComponentName('address-field-internal');

const GOOGLE_MAPS_CONNECTOR_TEMPLATE = 'google-maps-places';
const RADAR_CONNECTOR_TEMPLATE = 'radar';

const CONNECTOR_CLASSES = {
  [GOOGLE_MAPS_CONNECTOR_TEMPLATE]: GoogleMapsConnector,
  [RADAR_CONNECTOR_TEMPLATE]: RadarConnector,
};

const BaseInputClass = createBaseInputClass({
  componentName,
  baseSelector: '',
});
const initConnectorAttrs = ['public-api-key'];
const observedAttrs = [...initConnectorAttrs];

class RawAddressFieldInternal extends BaseInputClass {
  static get observedAttributes() {
    return [].concat(BaseInputClass.observedAttributes || [], observedAttrs);
  }

  get errorMsgValueMissing() {
    return (
      this.getAttribute('data-errormessage-value-missing') ||
      this.defaultErrorMsgValueMissing
    );
  }

  constructor() {
    super();

    this.innerHTML = `
    <div>
      <descope-autocomplete-field></descope-autocomplete-field>
    </div>
    `;

    this.autocompleteField = this.querySelector('descope-autocomplete-field');
  }

  get value() {
    return this.autocompleteField.value;
  }

  set value(val) {
    this.autocompleteField.value = val;
  }

  focus() {
    this.autocompleteField.focus();
  }

  init() {
    // This event listener needs to be placed before the super.init() call
    this.addEventListener('focus', (e) => {
      // we want to ignore focus events we are dispatching
      if (e.isTrusted) this.autocompleteField.focus();
    });

    super.init?.();
    this.initAutocomplete();
  }

  initAutocomplete() {
    forwardAttrs(this, this.autocompleteField, {
      includeAttrs: [
        'size',
        'bordered',
        'label',
        'label-type',
        'placeholder',
        'disabled',
        'readonly',
        'required',
        'full-width',
        'helper-text',
        'error-message',
        'default-value',
        'data-errormessage-value-missing',
        'st-host-direction',
        'allow-custom-value',
        'min-search-length',
        'st-error-message-icon',
        'st-error-message-icon-size',
        'st-error-message-icon-padding',
      ],
    });
    // This is required since when we remove the invalid attribute from the autocomplete field,
    // we want to reflect the change in the address field component
    syncAttrs(this, this.autocompleteField, { includeAttrs: ['invalid'] });

    // Bind the connector fetch results fn to the autocomplete field
    this.autocompleteField.fetchResults = this.fetchConnectorResults.bind(this);
  }

  attributeChangedCallback(attrName, oldValue, newValue) {
    super.attributeChangedCallback?.(attrName, oldValue, newValue);

    if (oldValue !== newValue) {
      if (initConnectorAttrs.includes(attrName)) {
        this.initializeConnector();
      }
    }
  }

  getValidity() {
    if (this.isRequired && !this.value) {
      return { valueMissing: true };
    }
    return { valid: true };
  }
}

export const AddressFieldInternal = compose(
  connectorMixin({ connectorClasses: CONNECTOR_CLASSES }),
)(RawAddressFieldInternal);
